import { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AccessibleDivButton } from '@/components/AccessibleDivButton';
import styles from './Snackbar.style';
import colors from '@/theme/colors';
import { BodyMediumMidEmphasisUnderline } from '../Typography';

const DEFAULT_DURATION_MS = 5000;
const EXTENSION_LIMIT = 10;
const EXTENSION_WINDOW_MS = 20000;
const SPACE_KEYS = [' ', 'Space', 'Spacebar'];
const EXTENSION_WINDOW_SECONDS = EXTENSION_WINDOW_MS / 1000;

interface ToastContentProps {
  id: string;
  message: string;
  onClick?: () => void;
  buttonLabel?: string;
  duration: number;
}

const restoreFocusToElement = (target: HTMLElement | null): void => {
  if (!target || !document.body.contains(target)) return;
  target.focus({ preventScroll: true });
};

const ToastContent: React.FC<ToastContentProps> = ({
  id,
  message,
  onClick,
  buttonLabel,
  duration,
}) => {
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const didRestoreFocusRef = useRef(false);
  const [extensionCount, setExtensionCount] = useState(0);

  const tryRestorePreviousFocus = useCallback((): void => {
    if (didRestoreFocusRef.current) return;
    didRestoreFocusRef.current = true;
    restoreFocusToElement(previousFocusRef.current);
  }, []);

  const { toasts } = useToasterStore();
  const isToastVisible = toasts.find(t => t.id === id)?.visible !== false;

  const clearDismissTimeout = useCallback(() => {
    if (!dismissTimeoutRef.current) return;
    clearTimeout(dismissTimeoutRef.current);
    dismissTimeoutRef.current = null;
  }, []);

  const scheduleDismiss = useCallback((ms: number = EXTENSION_WINDOW_MS) => {
    clearDismissTimeout();
    dismissTimeoutRef.current = setTimeout(() => toast.dismiss(id), ms);
  }, [clearDismissTimeout, id]);

  useEffect(() => {
    if (duration === Infinity) return;
    setExtensionCount(0);
    scheduleDismiss(duration);
    return () => {
      clearDismissTimeout();
    };
  }, [clearDismissTimeout, duration, scheduleDismiss]);

  useLayoutEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    didRestoreFocusRef.current = false;

    const attemptFocus = (): void => {
      containerRef.current?.focus({ preventScroll: true });
    };

    attemptFocus();
    const timeoutIds = [
      window.setTimeout(attemptFocus, 0),
      window.setTimeout(attemptFocus, 10),
      window.setTimeout(attemptFocus, 32),
    ];

    return (): void => {
      timeoutIds.forEach(tid => window.clearTimeout(tid));
      tryRestorePreviousFocus();
    };
  }, [tryRestorePreviousFocus]);

  useLayoutEffect(() => {
    if (isToastVisible) return;
    tryRestorePreviousFocus();
  }, [isToastVisible, tryRestorePreviousFocus]);

  useEffect(() => {
    if (!isToastVisible) return;
    if (duration === Infinity) return;

    const isSpaceKey = (e: KeyboardEvent): boolean =>
      SPACE_KEYS.includes(e.key) || e.code === 'Space';

    const handleDocumentKeyDown = (e: KeyboardEvent): void => {
      if (!isSpaceKey(e)) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      const activeElement = document.activeElement as HTMLElement | null;
      const isTypingElement = activeElement
        ? ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) ||
          activeElement.isContentEditable
        : false;

      if (isTypingElement) return;

      e.preventDefault();
      e.stopPropagation();

      setExtensionCount(previousCount => {
        if (previousCount >= EXTENSION_LIMIT) return previousCount;
        scheduleDismiss();
        return previousCount + 1;
      });
    };

    const preventSpaceScrollOnKeyUp = (e: KeyboardEvent): void => {
      if (!isSpaceKey(e)) return;
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    document.addEventListener('keyup', preventSpaceScrollOnKeyUp, true);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, true);
      document.removeEventListener('keyup', preventSpaceScrollOnKeyUp, true);
    };
  }, [duration, isToastVisible, scheduleDismiss]);

  const remainingExtensions = EXTENSION_LIMIT - extensionCount;
  const warningMessage =
    remainingExtensions > 0
      ? `This notification will close in ${EXTENSION_WINDOW_SECONDS} seconds. Press Space to extend by ${EXTENSION_WINDOW_SECONDS} seconds. ${remainingExtensions} extension${
          remainingExtensions === 1 ? '' : 's'
        } remaining.`
      : 'This notification will close soon.';

  return (
    <div
      ref={containerRef}
      className={styles.container}
      tabIndex={0}
      aria-label={message}
    >
      <span className={styles.message}>{message}</span>
      <span className="sr-only" aria-live="assertive" aria-atomic="true">
        {duration !== Infinity ? warningMessage : ''}
      </span>
      {onClick && buttonLabel ? (
        <AccessibleDivButton
          onClick={() => {
            onClick();
            toast.dismiss(id);
          }}
        >
          <BodyMediumMidEmphasisUnderline color={colors.shadesJungle700}>
            {buttonLabel}
          </BodyMediumMidEmphasisUnderline>
        </AccessibleDivButton>
      ) : (
        <IconButton
          onClick={() => toast.dismiss(id)}
          aria-label="Dismiss Notification"
          data-testid="dismissNotification"
        >
          <CloseIcon className={styles.closeIcon} />
        </IconButton>
      )}
    </div>
  );
};

export const useSnackbar = () => {
  const showToast = ({
    message,
    onClick,
    buttonLabel,
    duration = DEFAULT_DURATION_MS,
  }: {
    message: string;
    onClick?: () => void;
    buttonLabel?: string;
    duration?: number;
  }) => {
    toast(
      t => (
        <ToastContent
          id={t.id}
          message={message}
          onClick={onClick}
          buttonLabel={buttonLabel}
          duration={duration}
        />
      ),
      {
        duration: Infinity,
      }
    );
  };

  return showToast;
};

export const SnackbarProvider = () => {
  const { toasts } = useToasterStore();
  const TOAST_LIMIT = 1;

  useEffect(() => {
    toasts
      .filter(t => t.visible)
      .filter((_, i) => i >= TOAST_LIMIT)
      .forEach(t => toast.dismiss(t.id));
  }, [toasts]);

  return (
    <Toaster
      position="top-center"
      reverseOrder={false}
      gutter={0}
      toastOptions={{
        className: styles.toastStyle,
      }}
    />
  );
};
